// The source card's four facts — duration, resolution, fps — resolved field by
// field across the three places they can be known.
//
// THE WINDOW THIS EXISTS FOR. `project.video.length/resolution/fps` are written
// by the analyzevideo worker when it FINISHES (containers/analyzevideo/
// processing/motion_analysis.py), and the storage finalize trigger wipes them
// again the moment a new object claims the source slot
// (`_ANALYSIS_DERIVED_VIDEO_FIELDS` in functions/storage/background/main.py).
// So a fresh combine has a window — combine complete → analysis complete, about
// three minutes in production for a 466 MB MJPEG clip that also needs a proxy —
// where the card knows none of them and shows `--` three times, while the clip
// list directly beneath it shows the very same numbers. Those numbers were
// probed at ingest and sit on the footage docs (`projects/{id}/videos/{key}`),
// so the card can simply read them.
//
// TWO RULES THAT ARE NOT OBVIOUS:
//
//   * The MANIFEST decides which clips count, not the live clip list.
//     `videoConcat.manifest` is the ordered snapshot of exactly the objects the
//     combine consumed (the same snapshot `manifestDiff` compares against in
//     footageConcat.js). A clip uploaded AFTER the combine is not in the mp4,
//     so counting it would describe a video that does not exist. No manifest at
//     all means no known recipe — a plain upload — and the clips are ignored
//     entirely.
//
//   * A PARTIAL sum is refused. If any manifest clip is missing from the live
//     list, or is present but still being probed, the duration comes back null
//     and the card shows `--`. A duration that is too short is worse than no
//     duration: `--` reads as "not known yet", while `0:42` for a 1:19 video
//     reads as a fact, and the user has no way to tell it is wrong.
//
//   * A RE-UPLOADED clip counts as missing, not as present. storage.rules lets
//     an owner overwrite an existing footage object, so a clip can keep its
//     videoKey while holding different bytes — and different bytes mean a
//     different duration than the one inside the mp4. `manifestDiff` already
//     spots exactly this (its `resized` bucket, the signal behind the
//     "footage changed" banner), so the same comparison decides it here: a
//     clip whose stored size no longer matches the manifest's is not the clip
//     that was combined, and cannot be measured for it.
//
//     The window this closes is much wider than the three minutes above.
//     `video.length/resolution/fps` are only re-written by the analyzer on
//     SUCCESS, so after a failed or cancelled analysis the card reads from the
//     clips indefinitely — long enough for a user to replace a clip and
//     silently rewrite the reported duration of an mp4 that never changed.
//
// Analysis values always win when they are present, so this only ever fills a
// hole — it can never contradict the worker.

import { manifestDiff, normalizeClip } from './footageConcat';

// Absent, zero, negative, NaN and Infinity are all "not known". A stored 0 fps
// or 0-second duration is a failed probe, not a measurement, and rendering it
// would claim a fact we do not have.
const positive = (value) => (
    typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null
);

// Width and height travel together: a resolution is one fact in two numbers,
// and half of one (1920 x nothing) is not renderable. Reads `{width, height}`
// off either a `resolution` object or a normalized clip.
const dimensions = (source) => {
    const width = positive(source?.width);
    const height = positive(source?.height);
    return (width && height) ? { width, height } : null;
};

/**
 * What the clips the combine consumed say about the combined video.
 * Returns null when there is no manifest — i.e. this source is not a combine
 * with a known recipe, so its clips (if any) describe something else.
 */
function fromManifestClips(videoConcat, footageVideos) {
    const entries = Array.isArray(videoConcat?.manifest) ? videoConcat.manifest : null;
    if (!entries || entries.length === 0) return null;

    // normalizeClip coerces the same way the Python twins do, and folds the
    // doc's `id` onto `videoKey` — which is what the manifest names.
    const byKey = new Map();
    (Array.isArray(footageVideos) ? footageVideos : []).forEach((video) => {
        const clip = normalizeClip(video);
        if (clip.videoKey) byKey.set(clip.videoKey, clip);
    });

    // A clip that was re-uploaded after the combine kept its videoKey but not
    // its content, so it is not the clip that went into the mp4. manifestDiff's
    // `resized` bucket is precisely that set (live sizeBytes !== the manifest's
    // sizeBytes), so it is reused rather than re-derived here — one definition
    // of "this clip changed", shared with the Re-combine banner.
    const stale = new Set(manifestDiff(footageVideos, entries).resized);

    // Manifest ORDER, because "the first clip" below means the first one in the
    // video. An entry whose videoKey is missing — or names a clip we just
    // called stale — matches nothing, which is the right answer: a clip we
    // cannot identify as the one that was combined is one we cannot measure.
    const ordered = entries.map((entry) => (
        stale.has(entry?.videoKey) ? null : byKey.get(entry?.videoKey) || null
    ));

    const durations = ordered.map((clip) => positive(clip?.durationSec));
    const length = durations.every((seconds) => seconds !== null)
        ? durations.reduce((total, seconds) => total + seconds, 0)
        : null;

    // The combine REFUSES mismatched clips (compatibilityIssues in
    // footageConcat.js compares codec/width/height/fps across the set), so any
    // one clip that carries a value speaks for the whole output — but only
    // among the clips that were actually combined, which is why the stale ones
    // were dropped above rather than merely excluded from the sum.
    const present = ordered.filter(Boolean);
    const resolution = present.map(dimensions).find(Boolean) || null;
    const fps = present.map((clip) => positive(clip.fps)).find((value) => value !== null) ?? null;

    return { length, width: resolution?.width ?? null, height: resolution?.height ?? null, fps };
}

/**
 * Resolve the source card's metadata.
 *
 * Per FIELD, in order: the analysis result (`video`), then the in-session
 * upload probe (`localVideoMeta`), then the combine's clips. Each of length,
 * resolution and fps is resolved independently, so a partly-stamped video still
 * shows everything that is known.
 *
 * @param {object} input
 * @param {object} [input.video] `project.video` — the analysis result.
 * @param {object} [input.localVideoMeta] The browser's probe of a file uploaded this session.
 * @param {object} [input.videoConcat] `project.videoConcat` — carries the manifest.
 * @param {Array}  [input.footageVideos] The live footage clip docs.
 * @returns {{length: ?number, width: ?number, height: ?number, fps: ?number, fromClips: boolean}}
 */
export function resolveSourceVideoMeta(input) {
    const { video, localVideoMeta, videoConcat, footageVideos } = input || {};
    // Named `clipMeta`, not `clips`: it is one measurement OF the clip set, not
    // the set. (`clipMeta.length` is a duration in seconds, not a count.)
    const clipMeta = fromManifestClips(videoConcat, footageVideos);

    // Exposed so a caller (and these tests) can tell a probed number from a
    // measured one. The card does not render it today.
    let fromClips = false;
    const resolve = (analysis, local, clipValue) => {
        if (analysis !== null) return analysis;
        if (local !== null) return local;
        if (clipValue !== null) {
            fromClips = true;
            return clipValue;
        }
        return null;
    };

    const length = resolve(
        positive(video?.length),
        positive(localVideoMeta?.length),
        clipMeta ? positive(clipMeta.length) : null,
    );
    const resolution = resolve(
        dimensions(video?.resolution),
        dimensions(localVideoMeta?.resolution),
        clipMeta ? dimensions(clipMeta) : null,
    );
    // No localVideoMeta step: the in-session probe never carried an fps (the
    // browser's <video> element does not expose one), which is exactly why this
    // field was the first to read `--`.
    const fps = resolve(
        positive(video?.fps),
        null,
        clipMeta ? positive(clipMeta.fps) : null,
    );

    return {
        length,
        width: resolution?.width ?? null,
        height: resolution?.height ?? null,
        fps,
        fromClips,
    };
}
